import React, { memo } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { VIDEO_PROVIDER_URL } from "@app/constants/globalUrls";
import { useVideoEmbedToken } from "@videos/hooks/useVideoEmbedToken";
import { buildBrightcovePlayerIframeSrc } from "@videos/utils/buildBrightcovePlayerIframeSrc";
import { VideoEmbedTokenSource } from "@videos/models/embedToken";
import FeatureToggleService from "@services/FeatureToggleService";
import { ToggledFeature } from "@shared/models/featureToggle";

type Props = {
  videoId: string;
  videoTitle: string;
  legacyToken?: string | null;
  enabled?: boolean;
  embedTokenSource?: VideoEmbedTokenSource;
  className?: string;
  loadingClassName?: string;
  errorClassName?: string;
  testId?: string;
};

const VideoPlayer = ({
  videoId,
  videoTitle,
  legacyToken,
  enabled = true,
  embedTokenSource,
  className,
  loadingClassName,
  errorClassName,
  testId,
}: Props) => {
  const isBrightcoveEnabled = FeatureToggleService.isFeatureEnabled(ToggledFeature.BrightcoveVideo);
  const { embedToken, isLoading, error } = useVideoEmbedToken(
    videoId,
    enabled && isBrightcoveEnabled,
    embedTokenSource
  );
  const brightcoveIframeSrc = buildBrightcovePlayerIframeSrc(embedToken);

  if (isBrightcoveEnabled) {
    if (isLoading) {
      return (
        <Box className={loadingClassName ?? className} display="flex" justifyContent="center" alignItems="center">
          <CircularProgress />
        </Box>
      );
    }

    if (error || !brightcoveIframeSrc) {
      return (
        <Box className={errorClassName ?? className} display="flex" justifyContent="center" alignItems="center">
          <Typography variant="body2">{error ?? "Unable to load video."}</Typography>
        </Box>
      );
    }

    return (
      <iframe
        key={brightcoveIframeSrc}
        data-testid={testId ?? "brightcoveVideoIFrame"}
        className={className}
        title={videoTitle}
        frameBorder="0"
        allowFullScreen
        allow="encrypted-media"
        src={brightcoveIframeSrc}
      />
    );
  }

  return (
    <iframe
      data-testid={testId ?? "videoIFrame"}
      className={className}
      title={videoTitle}
      frameBorder="0"
      allowFullScreen
      src={`${VIDEO_PROVIDER_URL}/${videoId}/player?uss_token=${legacyToken ?? ""}`}
    />
  );
};

export default memo(VideoPlayer);
